import type { ReactNode } from 'react'

import {
  Clock01Icon,
  DiceFaces06Icon,
  Ticket01Icon
} from '@hugeicons/react'
import clsx from 'clsx'

export type PrizesIndicatorStats = {
  activeCount: number
  participatingCount: number
  /** Ex.: "em 3h 21m" — omitido quando não há próximo concurso. */
  nextDrawLabel?: string | null
}

type IndicatorItem = {
  key: string
  icon: ReactNode
  iconWrapClass: string
  value: string
  label: string
}

const iconWrapBase =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full'

const buildItems = (stats: PrizesIndicatorStats): IndicatorItem[] => [
  {
    key: 'active',
    icon: (
      <DiceFaces06Icon
        size={12}
        variant="solid"
        className="text-[#7c3aed]"
        aria-hidden
      />
    ),
    iconWrapClass: 'bg-[#ede9fe]',
    value: String(stats.activeCount),
    label: 'Concursos ativos'
  },
  {
    key: 'participating',
    icon: (
      <Ticket01Icon
        size={12}
        variant="solid"
        className="text-[#059669]"
        aria-hidden
      />
    ),
    iconWrapClass: 'bg-[#d1fae5]',
    value: String(stats.participatingCount),
    label: 'Participações'
  },
  {
    key: 'next',
    icon: (
      <Clock01Icon
        size={12}
        variant="solid"
        className="text-[#d97706]"
        aria-hidden
      />
    ),
    iconWrapClass: 'bg-[#ffedd5]',
    value: stats.nextDrawLabel ?? 'Em breve',
    label: 'Próximo concurso'
  }
]

type PrizesIndicatorBarProps = {
  stats: PrizesIndicatorStats
  className?: string
}

export const PrizesIndicatorBar = ({
  stats,
  className
}: PrizesIndicatorBarProps) => {
  const items = buildItems(stats)

  return (
    <div
      className={clsx(
        'relative z-[2] flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
        className
      )}
    >
      {items.map((item, index) => (
        <div key={item.key} className="flex min-w-0 flex-1">
          {index > 0 ? (
            <div className="w-px shrink-0 self-stretch bg-gray-200" aria-hidden />
          ) : null}
          <div className="flex min-w-0 flex-1 items-start gap-1.5 px-2 py-2.5 sm:gap-2 sm:px-3">
            <span className={clsx(iconWrapBase, item.iconWrapClass)}>
              {item.icon}
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-bold leading-tight text-gray-900">
                {item.value}
              </span>
              <span className="text-2xs font-medium leading-snug text-gray-700 [overflow-wrap:anywhere]">
                {item.label}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export const PrizesIndicatorBarSkeleton = ({ className }: { className?: string }) => (
  <div
    className={clsx(
      'relative z-[2] flex animate-pulse overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
      className
    )}
  >
    {[1, 2, 3].map(i => (
      <div
        key={i}
        className="flex min-w-0 flex-1 items-start gap-1.5 px-2 py-2.5 sm:gap-2 sm:px-3"
      >
        <span className="h-6 w-6 shrink-0 rounded-full bg-gray-200" />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="h-3.5 w-8 rounded bg-gray-200" />
          <span className="h-2.5 w-12 rounded bg-gray-200" />
        </div>
      </div>
    ))}
  </div>
)
